import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import './ApiMonitor.css';

export default function ApiMonitor() {
  const dailySummary = useQuery(api.apiUsage.getDailyUsageSummary);
  const circuitStatus = useQuery(api.apiUsage.getCircuitBreakerStatus);
  const recentErrors = useQuery(api.apiUsage.getRecentErrors, { limit: 10 });
  const usageStats = useQuery(api.apiUsage.getUsageStats, {});

  if (!dailySummary || !circuitStatus || !usageStats) {
    return (
      <div className="api-monitor">
        <div className="loading">Loading API monitoring data...</div>
      </div>
    );
  }

  const getCircuitBreakerColor = (state: string): string => {
    switch (state) {
      case 'closed':
        return 'success';
      case 'half-open':
        return 'warning';
      case 'open':
        return 'error';
      default:
        return 'unknown';
    }
  };

  const getCircuitBreakerEmoji = (state: string): string => {
    switch (state) {
      case 'closed':
        return '✅';
      case 'half-open':
        return '⚠️';
      case 'open':
        return '🔴';
      default:
        return '❓';
    }
  };

  const getQuotaPercentage = (used: number, total: number): number => {
    if (total === Infinity) return 0;
    return (used / total) * 100;
  };

  const getQuotaClass = (percentage: number): string => {
    if (percentage >= 90) return 'critical';
    if (percentage >= 75) return 'warning';
    return 'normal';
  };

  return (
    <div className="api-monitor">
      <div className="monitor-meta" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <div className="last-updated">Last updated: {new Date(dailySummary.timestamp).toLocaleTimeString()}</div>
      </div>

      {/* Daily Quota Usage */}
      <div className="monitor-section">
        <h3>📊 Today's API Usage</h3>
        <div className="quota-grid">
          {/* Search API */}
          <div className="quota-card">
            <div className="quota-header">
              <span className="service-name">Search API</span>
              <span className="quota-status normal">
                {dailySummary.summary.exa.requests} calls
              </span>
            </div>
            <div className="quota-details">
              <span>Unlimited</span>
              {dailySummary.summary.exa.cost > 0 && (
                <span className="cost">${dailySummary.summary.exa.cost.toFixed(2)}</span>
              )}
            </div>
          </div>

          {/* Backup Search */}
          {dailySummary.summary.brave && (
            <div className="quota-card">
              <div className="quota-header">
                <span className="service-name">Backup Search API</span>
                <span className="quota-status normal">
                  {dailySummary.summary.brave.requests} calls
                </span>
              </div>
              <div className="quota-details">
                <span>Unlimited</span>
                {dailySummary.summary.brave.cost > 0 && (
                  <span className="cost">${dailySummary.summary.brave.cost.toFixed(2)}</span>
                )}
              </div>
            </div>
          )}

          {/* Content Scraper */}
          <div className="quota-card">
            <div className="quota-header">
              <span className="service-name">Content Scraper</span>
              <span className="quota-status normal">
                {dailySummary.summary.firecrawl.requests} calls
              </span>
            </div>
            <div className="quota-details">
              <span>Unlimited</span>
              {dailySummary.summary.firecrawl.cost > 0 && (
                <span className="cost">${dailySummary.summary.firecrawl.cost.toFixed(2)}</span>
              )}
            </div>
          </div>

          {/* AI Classification */}
          <div className="quota-card">
            <div className="quota-header">
              <span className="service-name">AI Classification</span>
              <span className="quota-status normal">
                {dailySummary.summary.openrouter.calls} calls
              </span>
            </div>
            <div className="quota-details">
              <span>{dailySummary.summary.openrouter.tokens.toLocaleString()} tokens</span>
              <span className="cost">${dailySummary.summary.openrouter.cost.toFixed(2)}</span>
            </div>
          </div>

          {/* ipapi.co */}
          <div className="quota-card">
            <div className="quota-header">
              <span className="service-name">ipapi.co (Hosting)</span>
              <span className={`quota-status ${getQuotaClass(getQuotaPercentage(dailySummary.summary.ipapi.calls, dailySummary.quotas.ipapi.daily))}`}>
                {dailySummary.summary.ipapi.calls} / {dailySummary.quotas.ipapi.daily}
              </span>
            </div>
            <div className="quota-bar-container">
              <div
                className={`quota-bar ${getQuotaClass(getQuotaPercentage(dailySummary.summary.ipapi.calls, dailySummary.quotas.ipapi.daily))}`}
                style={{ width: `${Math.min(100, getQuotaPercentage(dailySummary.summary.ipapi.calls, dailySummary.quotas.ipapi.daily))}%` }}
              />
            </div>
            <div className="quota-details">
              <span>Remaining: {dailySummary.quotas.ipapi.remaining}</span>
            </div>
          </div>

          {/* WhoisXML */}
          <div className="quota-card">
            <div className="quota-header">
              <span className="service-name">WhoisXML (Domain)</span>
              <span className={`quota-status ${getQuotaClass(getQuotaPercentage(dailySummary.summary.whoisxml.calls, dailySummary.quotas.whoisxml.daily))}`}>
                {dailySummary.summary.whoisxml.calls} / {dailySummary.quotas.whoisxml.daily}
              </span>
            </div>
            <div className="quota-bar-container">
              <div
                className={`quota-bar ${getQuotaClass(getQuotaPercentage(dailySummary.summary.whoisxml.calls, dailySummary.quotas.whoisxml.daily))}`}
                style={{ width: `${Math.min(100, getQuotaPercentage(dailySummary.summary.whoisxml.calls, dailySummary.quotas.whoisxml.daily))}%` }}
              />
            </div>
            <div className="quota-details">
              <span>Remaining: {dailySummary.quotas.whoisxml.remaining}</span>
              {dailySummary.summary.whoisxml.cost > 0 && (
                <span className="cost">${dailySummary.summary.whoisxml.cost.toFixed(2)}</span>
              )}
            </div>
          </div>

          {/* Screenshot */}
          <div className="quota-card">
            <div className="quota-header">
              <span className="service-name">Screenshot</span>
              <span className={`quota-status ${getQuotaClass(getQuotaPercentage(dailySummary.summary.screenshot.calls, dailySummary.quotas.screenshot.daily))}`}>
                {dailySummary.summary.screenshot.calls} / {dailySummary.quotas.screenshot.daily}
              </span>
            </div>
            <div className="quota-bar-container">
              <div
                className={`quota-bar ${getQuotaClass(getQuotaPercentage(dailySummary.summary.screenshot.calls, dailySummary.quotas.screenshot.daily))}`}
                style={{ width: `${Math.min(100, getQuotaPercentage(dailySummary.summary.screenshot.calls, dailySummary.quotas.screenshot.daily))}%` }}
              />
            </div>
            <div className="quota-details">
              <span>Remaining: {dailySummary.quotas.screenshot.remaining}</span>
            </div>
          </div>

          {/* Resend */}
          <div className="quota-card">
            <div className="quota-header">
              <span className="service-name">Resend (Email)</span>
              <span className="quota-status normal">
                {dailySummary.summary.resend.calls} calls
              </span>
            </div>
            <div className="quota-details">
              <span>Unlimited</span>
              {dailySummary.summary.resend.cost > 0 && (
                <span className="cost">${dailySummary.summary.resend.cost.toFixed(2)}</span>
              )}
            </div>
          </div>

          {/* Trigger.dev */}
          <div className="quota-card">
            <div className="quota-header">
              <span className="service-name">Trigger.dev</span>
              <span className="quota-status normal">
                {dailySummary.summary.trigger.calls} calls
              </span>
            </div>
            <div className="quota-details">
              <span>Unlimited</span>
            </div>
          </div>
        </div>
      </div>

      {/* Circuit Breaker Status */}
      <div className="monitor-section">
        <h3>⚡ Circuit Breaker Status</h3>
        <div className="circuit-breaker-grid">
          {Object.entries(circuitStatus).map(([service, status]: [string, any]) => (
            <div key={service} className={`circuit-breaker-card ${getCircuitBreakerColor(status.state)}`}>
              <div className="circuit-header">
                <span className="circuit-service">{service}</span>
                <span className="circuit-indicator">{getCircuitBreakerEmoji(status.state)}</span>
              </div>
              <div className="circuit-status">
                <span className="status-label">Status:</span>
                <span className="status-value">{status.state.toUpperCase()}</span>
              </div>
              <div className="circuit-stats">
                <span>Recent calls: {status.totalCalls}</span>
                <span className={status.recentFailures > 0 ? 'failures' : ''}>
                  Failures: {status.recentFailures}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Usage Statistics */}
      <div className="monitor-section">
        <h3>📈 24h Usage Statistics</h3>
        <div className="stats-table">
          <table>
            <thead>
              <tr>
                <th>Service</th>
                <th>Calls</th>
                <th>Success Rate</th>
                <th>Avg Duration</th>
                <th>Retries</th>
                <th>Total Cost</th>
              </tr>
            </thead>
            <tbody>
              {usageStats.map((stat: any) => (
                <tr key={stat.service}>
                  <td className="service-cell">{stat.service}</td>
                  <td>{stat.totalCalls}</td>
                  <td className={stat.successRate >= 90 ? 'success' : stat.successRate >= 70 ? 'warning' : 'error'}>
                    {stat.successRate.toFixed(1)}%
                  </td>
                  <td>{stat.avgDurationMs.toFixed(0)}ms</td>
                  <td>{stat.retries}</td>
                  <td className="cost-cell">${stat.totalCost.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Errors */}
      {recentErrors && recentErrors.length > 0 && (
        <div className="monitor-section">
          <h3>❌ Recent Errors (Last Hour)</h3>
          <div className="errors-list">
            {recentErrors.map((error: any, idx: number) => (
              <div key={idx} className="error-card">
                <div className="error-header">
                  <span className="error-service">{error.service}</span>
                  <span className="error-time">
                    {new Date(error.timestamp).toLocaleTimeString()}
                  </span>
                </div>
                <div className="error-message">{error.errorMessage}</div>
                {error.retryCount && (
                  <div className="error-retries">Retried {error.retryCount} times</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
